import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Briefcase, MapPin, Building2, Clock, Activity, FileText } from 'lucide-react';
import { IntelligenceLayout, MetricCard, fetchWithAuth, CHART_COLORS } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm shadow-lg">
      <p className="text-[#0A1F44] font-medium">{label}</p>
      <p className="text-[#D4AF37] font-semibold">{payload[0].value.toLocaleString()} postings</p>
    </div>
  );
};

const IntelligenceDashboard = ({ embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchWithAuth(`${API_URL}/api/intelligence/summary`)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <IntelligenceLayout title="Executive Snapshot" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]">Executive Snapshot</h1>
            <p className="text-sm text-gray-500">Real-time job market intelligence for your recruiting business</p>
          </div>
          <div className="flex items-center gap-3">
            {data?.last_refresh && (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Clock className="h-3.5 w-3.5" />
                Last refreshed: {new Date(data.last_refresh).toLocaleString()}
              </div>
            )}
            <button
              onClick={() => navigate('/backoffice/intelligence/snapshot')}
              data-testid="generate-snapshot-report-link"
              className="flex items-center gap-1.5 text-sm text-[#0A1F44] border border-gray-200 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] rounded-lg px-3 py-1.5 transition-colors"
            >
              <FileText className="h-3.5 w-3.5" /> Snapshot Report
            </button>
          </div>
        </div>
      </header>

      <main className="p-8">
        {loading ? (
          <div className="text-center py-20 text-gray-400">
            <Activity className="h-8 w-8 animate-pulse mx-auto mb-3" />
            <p>Loading market data...</p>
          </div>
        ) : !data || data.total_postings_30d === 0 ? (
          <div className="text-center py-20">
            <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-600 mb-2">No Data Yet</p>
            <p className="text-sm text-gray-400 mb-4">Click "Refresh Data" in the sidebar to pull in job market data.</p>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="executive-metrics">
              <MetricCard label="Hottest Job Title" value={data.hottest_job?.title || 'N/A'} subtext={data.hottest_job ? `${data.hottest_job.count.toLocaleString()} postings` : ''} icon={TrendingUp} />
              <MetricCard label="Fastest Growing Role" value={data.fastest_growing?.title || 'N/A'} trend={data.fastest_growing?.growth_pct} subtext={data.fastest_growing ? `${data.fastest_growing.count} postings` : ''} icon={Activity} />
              <MetricCard label="Top Hiring Industry" value={data.top_industry?.name || 'N/A'} subtext={data.top_industry ? `${data.top_industry.count.toLocaleString()} postings` : ''} icon={Building2} />
              <MetricCard label="Top Hiring Region" value={data.top_region?.name || 'N/A'} subtext={data.top_region ? `${data.top_region.count.toLocaleString()} postings` : ''} icon={MapPin} />
              <MetricCard label="Total Postings" value={data.total_postings_30d.toLocaleString()} subtext="Last 30 days" icon={Briefcase} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="top-titles-chart">
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-4 uppercase tracking-wider">Most Posted Job Titles</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.top_titles.slice(0, 8)} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                    <YAxis type="category" dataKey="title" width={140} tick={{ fill: '#374151', fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#D4AF37" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="fastest-growing-chart">
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-4 uppercase tracking-wider">Fastest Growing Roles</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.fastest_growing_titles.slice(0, 8).map(t => ({ ...t, growth_display: Math.max(0, t.growth_pct) }))} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} unit="%" />
                    <YAxis type="category" dataKey="title" width={140} tick={{ fill: '#374151', fontSize: 11 }} />
                    <Tooltip content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm shadow-lg">
                          <p className="text-[#0A1F44] font-medium">{label}</p>
                          <p className="text-emerald-600">+{payload[0].payload.growth_pct}% MoM</p>
                          <p className="text-gray-500">{payload[0].payload.current_count} postings</p>
                        </div>
                      );
                    }} />
                    <Bar dataKey="growth_display" fill="#059669" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="top-industries-chart">
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-4 uppercase tracking-wider">Top Industries Hiring</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.top_industries.slice(0, 8)} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fill: '#374151', fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#2563eb" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="top-markets-chart">
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-4 uppercase tracking-wider">Top Geographic Markets</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.top_states.slice(0, 8)} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fill: '#374151', fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs text-gray-500">
              <p>Data sourced from multiple verified public APIs. Coverage varies by region and industry. Metrics based on available data — not a complete census.</p>
            </div>
          </div>
        )}
      </main>
    </IntelligenceLayout>
  );
};

export default IntelligenceDashboard;
